import { useState, useMemo } from 'react';
import { useAdminSettings } from '@/hooks/useAdminSettings';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { z } from 'zod';
import { format, addYears, addMonths } from 'date-fns';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { CalendarIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

const clientSchema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(100),
  email: z.string().trim().email('Invalid email').max(255),
  plan: z.string().min(1, 'Plan is required'),
  startDate: z.date({ required_error: 'Start date is required' }),
  paymentDone: z.boolean(),
});

type ClientFormData = z.infer<typeof clientSchema>;

const getPlans = (monthlyPrice: string, lifetimePrice: string) => [
  { value: 'Lifetime', price: parseFloat(lifetimePrice), duration: 'year' as const, label: `Lifetime — $${lifetimePrice}` },
  { value: 'Monthly', price: parseFloat(monthlyPrice), duration: 'month' as const, label: `Monthly — $${monthlyPrice}/mo` },
  { value: 'Test_Lifetime', price: 0, duration: 'year' as const, label: '🧪 Test Lifetime (Free)' },
  { value: 'Test_Monthly', price: 0, duration: 'month' as const, label: '🧪 Test Monthly (Free)' },
];

interface CreateClientDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: {
    name: string;
    email: string;
    plan: string;
    plan_price: number;
    status: 'active' | 'expiring' | 'expired' | 'inactive';
    paid: boolean;
    has_access: boolean;
    subscription_start: string;
    subscription_end: string;
    user_id: null;
    last_active_at: string | null;
  }) => Promise<void>;
}

export const CreateClientDialog = ({ isOpen, onClose, onSubmit }: CreateClientDialogProps) => {
  const { monthlyPrice, lifetimePrice } = useAdminSettings();
  const planOptions = useMemo(() => getPlans(monthlyPrice, lifetimePrice), [monthlyPrice, lifetimePrice]);
  const [formData, setFormData] = useState<Partial<ClientFormData>>({
    name: '',
    email: '',
    plan: '',
    startDate: new Date(),
    paymentDone: false,
  });
  const [errors, setErrors] = useState<Partial<Record<keyof ClientFormData, string>>>({});
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const result = clientSchema.safeParse(formData);
    if (!result.success) {
      const fieldErrors: Partial<Record<keyof ClientFormData, string>> = {};
      result.error.errors.forEach((err) => {
        if (err.path[0]) {
          fieldErrors[err.path[0] as keyof ClientFormData] = err.message;
        }
      });
      setErrors(fieldErrors);
      return;
    }

    setErrors({});
    setIsLoading(true);

    const selectedPlan = planOptions.find((p) => p.value === result.data.plan);
    const startDate = result.data.startDate;
    const endDate = selectedPlan?.duration === 'month' 
      ? addMonths(startDate, 1) 
      : addYears(startDate, 100); // 100 years for lifetime/test_lifetime

    // Test accounts automatically get access without payment
    const isTestPlan = result.data.plan.toLowerCase().startsWith('test_');
    const hasAccess = result.data.paymentDone || isTestPlan;

    try {
      await onSubmit({
        name: result.data.name,
        email: result.data.email,
        plan: result.data.plan,
        plan_price: selectedPlan?.price || 0,
        status: hasAccess ? 'active' : 'inactive',
        paid: result.data.paymentDone,
        has_access: hasAccess,
        subscription_start: format(startDate, 'yyyy-MM-dd'),
        subscription_end: format(endDate, 'yyyy-MM-dd'),
        user_id: null,
        last_active_at: hasAccess ? new Date().toISOString() : null,
      });
      // Reset form
      setFormData({
        name: '',
        email: '',
        plan: '',
        startDate: new Date(),
        paymentDone: false,
      });
      onClose();
    } catch (error) {
      console.error('Error creating client:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 flex items-center justify-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-background/80 backdrop-blur-sm"
          onClick={onClose}
        />

        {/* Modal */}
        <motion.div
          className="relative z-10 w-full max-w-md mx-4 p-6 border border-border/30 bg-card rounded-lg shadow-xl"
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-light text-foreground">Create New Client</h2>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Name */}
            <div className="space-y-2">
              <Label htmlFor="name" className="text-sm text-muted-foreground">
                Name
              </Label>
              <Input
                id="name"
                value={formData.name || ''}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="bg-background/50 border-border/30"
                placeholder="John Doe"
              />
              {errors.name && <p className="text-red-400 text-xs">{errors.name}</p>}
            </div>

            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm text-muted-foreground">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={formData.email || ''}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="bg-background/50 border-border/30"
                placeholder="john@example.com"
              />
              {errors.email && <p className="text-red-400 text-xs">{errors.email}</p>}
            </div>

            {/* Plan */}
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">Plan</Label>
              <Select
                value={formData.plan || ''}
                onValueChange={(value) => setFormData({ ...formData, plan: value })}
              >
                <SelectTrigger className="bg-background/50 border-border/30">
                  <SelectValue placeholder="Select a plan" />
                </SelectTrigger>
                <SelectContent>
                  {planOptions.map((plan) => (
                    <SelectItem key={plan.value} value={plan.value}>
                      {plan.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.plan && <p className="text-red-400 text-xs">{errors.plan}</p>}
            </div>

            {/* Start Date */}
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">Start Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      'w-full justify-start text-left font-normal bg-background/50 border-border/30',
                      !formData.startDate && 'text-muted-foreground'
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {formData.startDate ? (
                      format(formData.startDate, 'PPP')
                    ) : (
                      <span>Pick a date</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={formData.startDate}
                    onSelect={(date) => setFormData({ ...formData, startDate: date })}
                    initialFocus
                    className={cn('p-3 pointer-events-auto')}
                  />
                </PopoverContent>
              </Popover>
              {errors.startDate && <p className="text-red-400 text-xs">{errors.startDate}</p>}
            </div>

            {/* Payment Done */}
            <div className="flex items-center space-x-2 pt-2">
              <Checkbox
                id="paymentDone"
                checked={formData.paymentDone || false}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, paymentDone: checked === true })
                }
              />
              <Label htmlFor="paymentDone" className="text-sm text-muted-foreground cursor-pointer">
                Payment completed
              </Label>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="ghost"
                className="flex-1"
                onClick={onClose}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button type="submit" variant="default" className="flex-1" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Create Client'
                )}
              </Button>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CreateClientDialog;
